import { supabaseClientService, type SupabaseClientService } from "@/services/supabaseClientService";
import {
  buildInitialTasks,
  getGuestSummary,
  PLANNER_ENTITLEMENT,
  splitPriorityText,
  type BudgetItem,
  type PlannerBundle,
  type PlannerEntitlementStatus,
  type PlannerOnboardingInput,
  type PlanningTask,
  type ScheduleItem,
  type WeddingGuest,
  type WeddingPlanner,
  type WeddingPlannerProfile,
  type WeddingPriority,
  type WeddingVendor,
  type WeddingVendorCategory,
} from "@/lib/weddingPlanner";
import type { PlannerPlan } from "@/lib/plannerPricing";

type SupabaseUser = { id: string; email?: string | null };
type CheckoutContact = { name: string; email: string; phone?: string | null };
type PlannerRegistrationInput = { name?: string; email: string; password: string; locale?: string };

export interface WeddingPlannerService {
  getCurrentUser(): Promise<SupabaseUser | null>;
  listMyPlanners(): Promise<WeddingPlanner[]>;
  getPlannerBundle(plannerId: string): Promise<PlannerBundle | null>;
  getEntitlementStatus(): Promise<PlannerEntitlementStatus>;
  registerPlannerAccount(input: PlannerRegistrationInput): Promise<{ sessionStarted: boolean; existingUser?: boolean; requiresLogin?: boolean; message?: string }>;
  createPlannerFromOnboarding(input: PlannerOnboardingInput): Promise<WeddingPlanner>;
  createPlannerCheckout(input: {
    plan: PlannerPlan;
    contact: CheckoutContact;
    successUrl: string;
    cancelUrl: string;
    locale?: string;
  }): Promise<{ url: string }>;
  createTask(plannerId: string, input: Partial<PlanningTask> & { title: string }): Promise<PlanningTask>;
  updateTask(taskId: string, patch: Partial<PlanningTask>): Promise<PlanningTask>;
  deleteTask(taskId: string): Promise<void>;
  createGuest(plannerId: string, input: Partial<WeddingGuest> & { full_name: string }): Promise<WeddingGuest>;
  updateGuest(guestId: string, patch: Partial<WeddingGuest>): Promise<WeddingGuest>;
  deleteGuest(guestId: string): Promise<void>;
  createBudgetItem(plannerId: string, input: Partial<BudgetItem> & { category: string; item: string }): Promise<BudgetItem>;
  updateBudgetItem(itemId: string, patch: Partial<BudgetItem>): Promise<BudgetItem>;
  createVendorCategory(plannerId: string, input: Partial<WeddingVendorCategory> & { group_key: string; category_key: string; label: string }): Promise<WeddingVendorCategory>;
  updateVendorCategory(categoryId: string, patch: Partial<WeddingVendorCategory>): Promise<WeddingVendorCategory>;
  deleteVendorCategory(categoryId: string): Promise<void>;
  createVendor(plannerId: string, input: Partial<WeddingVendor> & { vendor_type: string; name: string }): Promise<WeddingVendor>;
  updateVendor(vendorId: string, patch: Partial<WeddingVendor>): Promise<WeddingVendor>;
  createScheduleItem(plannerId: string, input: Partial<ScheduleItem> & { action: string }): Promise<ScheduleItem>;
  updateScheduleItem(itemId: string, patch: Partial<ScheduleItem>): Promise<ScheduleItem>;
}

const dbFrom = (client: SupabaseClientService) => client.from as any;

const requireUser = async (client: SupabaseClientService): Promise<SupabaseUser> => {
  const { data, error } = await client.auth.getUser();
  if (error || !data?.user) {
    throw new Error("Please sign in to use the wedding planner.");
  }
  return { id: data.user.id, email: data.user.email };
};

const maybeNumber = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  "€": "EUR",
  "$": "USD",
  "£": "GBP",
};

const normalizeCurrencyCode = (value: string | null | undefined) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "EUR";

  const mapped = CURRENCY_SYMBOL_MAP[trimmed];
  if (mapped) return mapped;

  const upper = trimmed.toUpperCase();
  if (/^[A-Z]{3}$/.test(upper)) return upper;

  return "EUR";
};

export const createWeddingPlannerService = (client: SupabaseClientService = supabaseClientService): WeddingPlannerService => {
  const from = dbFrom(client);

  return {
    async getCurrentUser() {
      const { data, error } = await client.auth.getUser();
      if (error || !data?.user) return null;
      return { id: data.user.id, email: data.user.email };
    },

    async listMyPlanners() {
      await requireUser(client);
      const { data, error } = await from("wedding_planners")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as WeddingPlanner[];
    },

    async getEntitlementStatus() {
      const user = await this.getCurrentUser();
      if (!user) return { hasAccess: false };

      const { data: userEntitlement, error: userError } = await from("wedding_planner_entitlements")
        .select("entitlement, plan, status")
        .eq("owner_user_id", user.id)
        .eq("status", "active")
        .maybeSingle();
      if (userError) throw userError;
      if (userEntitlement) return { hasAccess: true, ...userEntitlement };

      const email = user.email?.trim().toLowerCase();
      if (!email) return { hasAccess: false };

      const { data: emailEntitlement, error: emailError } = await from("wedding_planner_entitlements")
        .select("entitlement, plan, status")
        .eq("buyer_email", email)
        .eq("status", "active")
        .maybeSingle();
      if (emailError) throw emailError;
      return emailEntitlement ? { hasAccess: true, ...emailEntitlement } : { hasAccess: false };
    },

    async registerPlannerAccount(input) {
      const { data, error } = await client.functions.invoke("planner-registration", {
        body: input,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Could not create planner account.");
      if (data.session?.access_token && data.session?.refresh_token) {
        const { error: sessionError } = await client.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        if (sessionError) throw sessionError;
        return { sessionStarted: true, existingUser: !!data.existing_user };
      }
      return {
        sessionStarted: false,
        existingUser: !!data.existing_user,
        requiresLogin: !!data.requires_login,
        message: data.message,
      };
    },

    async getPlannerBundle(plannerId) {
      const entitlement = await this.getEntitlementStatus();
      const { data: planner, error: plannerError } = await from("wedding_planners")
        .select("*")
        .eq("id", plannerId)
        .maybeSingle();
      if (plannerError) throw plannerError;
      if (!planner) return null;

      const [profile, priorities, tasks, guests, budgetItems, vendorCategories, vendors, scheduleItems] = await Promise.all([
        from("wedding_planner_profiles").select("*").eq("planner_id", plannerId).maybeSingle(),
        from("wedding_planner_priorities").select("*").eq("planner_id", plannerId).order("created_at", { ascending: true }),
        from("wedding_planner_tasks").select("*").eq("planner_id", plannerId).order("due_date", { ascending: true, nullsFirst: false }),
        from("wedding_planner_guests").select("*").eq("planner_id", plannerId).order("full_name", { ascending: true }),
        from("wedding_planner_budget_items").select("*").eq("planner_id", plannerId).order("created_at", { ascending: true }),
        from("wedding_planner_vendor_categories").select("*").eq("planner_id", plannerId).order("sort_order", { ascending: true }),
        from("wedding_planner_vendors").select("*").eq("planner_id", plannerId).order("vendor_type", { ascending: true }),
        from("wedding_planner_schedule_items").select("*").eq("planner_id", plannerId).order("time", { ascending: true, nullsFirst: false }),
      ]);

      for (const response of [profile, priorities, tasks, guests, budgetItems, vendorCategories, vendors, scheduleItems]) {
        if (response.error) throw response.error;
      }

      return {
        planner: planner as WeddingPlanner,
        profile: (profile.data ?? null) as WeddingPlannerProfile | null,
        priorities: (priorities.data ?? []) as WeddingPriority[],
        tasks: (tasks.data ?? []) as PlanningTask[],
        guests: (guests.data ?? []) as WeddingGuest[],
        budgetItems: (budgetItems.data ?? []) as BudgetItem[],
        vendorCategories: (vendorCategories.data ?? []) as WeddingVendorCategory[],
        vendors: (vendors.data ?? []) as WeddingVendor[],
        scheduleItems: (scheduleItems.data ?? []) as ScheduleItem[],
        entitlement,
      };
    },

    async createPlannerFromOnboarding(input) {
      const user = await requireUser(client);
      const title = [input.partner1Name, input.partner2Name].filter(Boolean).join(" & ") || "Wedding planner";

      const { data: planner, error: plannerError } = await from("wedding_planners")
        .insert({ owner_user_id: user.id, title })
        .select("*")
        .single();
      if (plannerError) throw plannerError;

      const plannerId = planner.id as string;
      const { error: profileError } = await from("wedding_planner_profiles").insert({
        planner_id: plannerId,
        partner1_name: input.partner1Name.trim() || null,
        partner2_name: input.partner2Name.trim() || null,
        wedding_date: input.weddingDate || null,
        wedding_location: input.weddingLocation?.trim() || null,
        approximate_budget: maybeNumber(input.approximateBudget),
        expected_guest_count: maybeNumber(input.expectedGuestCount),
        currency: normalizeCurrencyCode(input.currency),
        wedding_style: input.weddingStyle?.trim() || null,
        planning_mode: input.planningMode,
        venue_booked: input.venueBooked,
        guest_list_started: input.guestListStarted,
      });
      if (profileError) throw profileError;

      const priorities = [
        ...splitPriorityText(input.topPriorities).map((priority_text) => ({
          planner_id: plannerId,
          priority_text,
          priority_type: "top_priority",
          visibility: "private_to_couple",
        })),
        ...splitPriorityText(input.notImportant).map((priority_text) => ({
          planner_id: plannerId,
          priority_text,
          priority_type: "not_important",
          visibility: "private_to_couple",
        })),
      ];
      if (priorities.length > 0) {
        const { error } = await from("wedding_planner_priorities").insert(priorities);
        if (error) throw error;
      }

      const initialTasks = buildInitialTasks(input, plannerId);
      if (initialTasks.length > 0) {
        const { error } = await from("wedding_planner_tasks").insert(initialTasks);
        if (error) throw error;
      }

      if (input.expectedGuestCount && input.expectedGuestCount > 0 && input.guestListStarted) {
        const { error } = await from("wedding_planner_guests").insert({
          planner_id: plannerId,
          full_name: "Guest list draft",
          group_name: "Planning estimate",
          invited_quantity: input.expectedGuestCount,
          rsvp_status: "waiting",
        });
        if (error) throw error;
      }

      if (input.approximateBudget && input.approximateBudget > 0) {
        const { error } = await from("wedding_planner_budget_items").insert({
          planner_id: plannerId,
          category: "Overall",
          item: "Planning budget",
          stage: "idea",
          budgeted_amount: input.approximateBudget,
        });
        if (error) throw error;
      }

      return planner as WeddingPlanner;
    },

    async createPlannerCheckout(input) {
      const { data, error } = await client.functions.invoke("create-checkout-session", {
        body: {
          plannerPurchase: { plan: input.plan },
          contact: input.contact,
          successUrl: input.successUrl,
          cancelUrl: input.cancelUrl,
          locale: input.locale || "en",
        },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("Planner checkout did not return a Stripe URL.");
      return { url: data.url };
    },

    async createTask(plannerId, input) {
      const { data, error } = await from("wedding_planner_tasks")
        .insert({ planner_id: plannerId, status: "not_started", priority: "medium", system_generated: false, ...input })
        .select("*")
        .single();
      if (error) throw error;
      return data as PlanningTask;
    },

    async updateTask(taskId, patch) {
      const { data, error } = await from("wedding_planner_tasks").update(patch).eq("id", taskId).select("*").single();
      if (error) throw error;
      return data as PlanningTask;
    },

    async deleteTask(taskId) {
      const { error } = await from("wedding_planner_tasks").delete().eq("id", taskId);
      if (error) throw error;
    },

    async createGuest(plannerId, input) {
      const { data, error } = await from("wedding_planner_guests")
        .insert({ planner_id: plannerId, invited_quantity: 1, rsvp_status: "waiting", thank_you_sent: false, ...input })
        .select("*")
        .single();
      if (error) throw error;
      return data as WeddingGuest;
    },

    async updateGuest(guestId, patch) {
      const { data, error } = await from("wedding_planner_guests").update(patch).eq("id", guestId).select("*").single();
      if (error) throw error;
      return data as WeddingGuest;
    },

    async deleteGuest(guestId) {
      const { error } = await from("wedding_planner_guests").delete().eq("id", guestId);
      if (error) throw error;
    },

    async createBudgetItem(plannerId, input) {
      const { data, error } = await from("wedding_planner_budget_items")
        .insert({ planner_id: plannerId, stage: "idea", payment_status: "unknown", ...input })
        .select("*")
        .single();
      if (error) throw error;
      return data as BudgetItem;
    },

    async updateBudgetItem(itemId, patch) {
      const { data, error } = await from("wedding_planner_budget_items").update(patch).eq("id", itemId).select("*").single();
      if (error) throw error;
      return data as BudgetItem;
    },

    async createVendorCategory(plannerId, input) {
      const { data, error } = await from("wedding_planner_vendor_categories")
        .insert({
          planner_id: plannerId,
          source: "preset",
          is_enabled: true,
          is_complete: false,
          sort_order: 0,
          ...input,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as WeddingVendorCategory;
    },

    async updateVendorCategory(categoryId, patch) {
      const { data, error } = await from("wedding_planner_vendor_categories").update(patch).eq("id", categoryId).select("*").single();
      if (error) throw error;
      return data as WeddingVendorCategory;
    },

    async deleteVendorCategory(categoryId) {
      const { error: detachError } = await from("wedding_planner_vendors")
        .update({ category_id: null, is_selected: false })
        .eq("category_id", categoryId);
      if (detachError) throw detachError;

      const { error } = await from("wedding_planner_vendor_categories").delete().eq("id", categoryId);
      if (error) throw error;
    },

    async createVendor(plannerId, input) {
      const { data, error } = await from("wedding_planner_vendors")
        .insert({ planner_id: plannerId, stage_of_communication: "not_contacted", ...input })
        .select("*")
        .single();
      if (error) throw error;
      return data as WeddingVendor;
    },

    async updateVendor(vendorId, patch) {
      const { data, error } = await from("wedding_planner_vendors").update(patch).eq("id", vendorId).select("*").single();
      if (error) throw error;
      return data as WeddingVendor;
    },

    async createScheduleItem(plannerId, input) {
      const { data, error } = await from("wedding_planner_schedule_items")
        .insert({ planner_id: plannerId, schedule_type: "run_of_show", ...input })
        .select("*")
        .single();
      if (error) throw error;
      return data as ScheduleItem;
    },

    async updateScheduleItem(itemId, patch) {
      const { data, error } = await from("wedding_planner_schedule_items").update(patch).eq("id", itemId).select("*").single();
      if (error) throw error;
      return data as ScheduleItem;
    },
  };
};

export const weddingPlannerService = createWeddingPlannerService();

export { getGuestSummary };
